import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { useToast } from '../../context/ToastContext';
import { useFiscalYear } from '../../context/FiscalYearContext';
import { CommentTextarea } from '../ui/CommentTextarea';
import {
  X, FileText, FileSpreadsheet, Loader2, ImageIcon,
  Trash2, BarChart3, ChevronDown, ChevronUp, FileSearch,
 AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../../utils/apiConfig';
import { optimisticSave } from '../../utils/optimisticSave';
import { formatReportRatio, formatReportTco2e, reportGroupDecimals } from '../../utils/emissionsFormat';

const SCOPE_COLORS = {
  scope1: '#FB923C',
  scope2: '#60A5FA',
  scope3_amont: '#A78BFA',
  scope3_aval: '#F9A8D4',
};

const ExportPreviewModal = ({ show, onClose, reportingView }) => {
  const { isDark } = useTheme();
  const { t, language } = useLanguage();
  const { showError } = useToast();
  const { currentFiscalYear, applyFreshFiscalYear } = useFiscalYear();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [logoBase64, setLogoBase64] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [comments, setComments] = useState({ intro: '', results: '', perimeter: '', methodology: '', objectives: '' });
  const [commentsOpen, setCommentsOpen] = useState(false);
  const fileRef = useRef(null);

  const fetchPreview = useCallback(async () => {
    if (!show) return;
    setLoading(true);
    try {
      const fyId = currentFiscalYear?.id || '';
      const res = await axios.get(`${API_URL}/api/export/report/preview?fiscal_year_id=${fyId}&reporting_view=${reportingView || 'market'}&language=${language}`);
      setPreview(res.data);
      const savedLogo = res.data?.company?.report_logo_base64;
      if (savedLogo) {
        setLogoBase64(savedLogo);
        setLogoPreview(`data:image/png;base64,${savedLogo}`);
      }
      const saved = res.data?.report_comments || {};
      setComments({ intro: saved.intro || '', results: saved.results || '', perimeter: saved.perimeter || '', methodology: saved.methodology || '', objectives: saved.objectives || '' });
    } catch (err) {
      console.error('Preview fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, [show, currentFiscalYear, reportingView, language]);

  useEffect(() => { fetchPreview(); }, [fetchPreview]);

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // RGPD-4.1 : pré-validation client (filet de sécurité — backend
    // re-valide en 413/400)
    if (file.size > 1024 * 1024) {
      showError(t('companies.logo.tooLarge'));
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
      showError(t('companies.logo.invalidType'));
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const b64 = reader.result.split(',')[1];
      setLogoPreview(reader.result);
      setLogoBase64(b64);
      try {
        await axios.put(`${API_URL}/api/companies/logo`, { logo_base64: b64 });
      } catch (err) {
        console.error('Logo save failed:', err);
        // Bypass frontend → backend renvoie 413 ou 400. Reset preview + message.
        const status = err?.response?.status;
        if (status === 413) {
          showError(t('companies.logo.tooLarge'));
        } else if (status === 400) {
          showError(t('companies.logo.invalidType'));
        } else {
          showError(t('companies.logo.uploadError'));
        }
        setLogoPreview(null);
        setLogoBase64(null);
        if (fileRef.current) fileRef.current.value = '';
      }
    };
    reader.readAsDataURL(file);
  };

  const saveComments = async (updated) => {
    const fyId = currentFiscalYear?.id;
    if (!fyId) return;
    try {
      const result = await optimisticSave(t, () =>
        axios.put(`${API_URL}/api/fiscal-years/${fyId}/context`, {
          report_comments: updated,
          updated_at: currentFiscalYear?.updated_at,
        })
      );
      // 409 : commentaire NON persisté — toast conflit déjà émis par le bridge.
      if (!result.ok) return;
      // FIX 409 sentinel périmé : resync updated_at depuis le doc frais du PUT,
      // sinon le blur du champ suivant 409 (1 save par blur).
      applyFreshFiscalYear(result.data);
    } catch (err) {
      console.error('Comments save failed:', err);
      // Échec non-409 (réseau/500) : rendre l'échec visible (commentaire non persisté).
      showError(t('errors.generic') + ': ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleCommentBlur = (key, value) => {
    const updated = { ...comments, [key]: value };
    setComments(updated);
    saveComments(updated);
  };

  const removeLogo = async () => {
    setLogoBase64(null);
    setLogoPreview(null);
    if (fileRef.current) fileRef.current.value = '';
    try {
      await axios.delete(`${API_URL}/api/companies/logo`);
    } catch (err) {
      console.error('Logo delete failed:', err);
    }
  };

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const fyId = currentFiscalYear?.id || '';
      let response;
      if (format === 'pdf') {
        response = await axios.post(`${API_URL}/api/export/report/pdf`, {
          fiscal_year_id: fyId,
          reporting_view: reportingView || 'market',
          logo_base64: logoBase64 || null,
          language,
        }, { responseType: 'blob' });
      } else if (format === 'appendix') {
        // ENH-03 LOT 2 — annexe détaillée paysage (calque exact du payload 'pdf')
        response = await axios.post(`${API_URL}/api/export/report/pdf/appendix`, {
          fiscal_year_id: fyId,
          reporting_view: reportingView || 'market',
          logo_base64: logoBase64 || null,
          language,
        }, { responseType: 'blob' });
      } else {
        response = await axios.get(
          `${API_URL}/api/export/report/excel?fiscal_year_id=${fyId}&reporting_view=${reportingView || 'market'}&language=${language}`,
          { responseType: 'blob' },
        );
      }
      const blob = new Blob([response.data]);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?(.+?)"?$/);
      // Fallback filename : Excel → .xlsx, sinon (pdf | appendix) → .pdf
      link.download = match ? match[1] : `rapport.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(null);
    }
  };

  if (!show) return null;

  const total = preview?.total_tco2e || 0;
  const scopes = preview?.scope_emissions || {};
  const kpis = preview?.kpis || {};
  const cats = preview?.category_breakdown || [];
  // Lot 1 quinquies (D9) : total, scopes et catégories = groupes ancrés sur le total du
  // bilan (D6), comme les cartes et le tableau du PDF ; ratios à 2 décimales fixes (D8).
  const dec = reportGroupDecimals(total);

  const bg = isDark ? 'bg-slate-900' : 'bg-white';
  const card = isDark ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200';
  const text = isDark ? 'text-white' : 'text-gray-900';
  const muted = isDark ? 'text-slate-400' : 'text-gray-500';

  const ep = (key) => t(`dashboard.exportPreview.${key}`);
  // Lot 2 zéro (D13/D14) : avertissement INFORMATIF sur un exercice dupliqué peu retravaillé.
  // `t()` n'interpole pas : jetons remplacés ici. Jamais bloquant : les boutons d'export restent actifs.
  const duplication = preview?.duplication || null;
  const fill = (key, vars) => Object.entries(vars).reduce((acc, [k, v]) => acc.replace(`{${k}}`, String(v)), ep(key));
  // Lot 2 zéro ter (D20/D21) : trois formulations — rouge à zéro, rouge non nul, ambre — plus
  // l'accord au singulier (n = 1, ou une seule activité). Le palier vient du backend ; la
  // distinction zéro / non-zéro se lit sur le compteur déjà reçu, sans palier supplémentaire.
  const reworkMessageKey = ({ level, activities_modified: n, activities_total: total }) => {
    if (level === 'critical') {
      if (n === 0) return total === 1 ? 'reworkCriticalZeroOne' : 'reworkCriticalZero';
      return n === 1 ? 'reworkCriticalFewOne' : 'reworkCriticalFew';
    }
    return n === 1 ? 'reworkWarningOne' : 'reworkWarning';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="export-preview-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full max-w-2xl max-h-[85vh] rounded-2xl shadow-2xl ${bg} flex flex-col overflow-hidden`}>

        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
          <div>
            <h2 className={`text-lg font-bold ${text}`}>{ep('title')}</h2>
            <p className={`text-sm ${muted}`}>
              {preview?.company?.name} — {preview?.fiscal_year?.name}
              {duplication?.source_fy_name && (
                <span className="ml-2 text-xs italic" data-testid="duplicated-from">
                  ({fill('duplicatedFrom', { name: duplication.source_fy_name })})
                </span>
              )}
            </p>
          </div>
          <button onClick={onClose} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : preview ? (
            <>
              {/* Logo upload */}
              <div>
                <label className={`block text-xs font-semibold uppercase tracking-wider mb-2 ${muted}`}>
                  {ep('logo')}
                </label>
                {logoPreview ? (
                  <div className={`flex items-center gap-4 p-3 rounded-xl border ${card}`}>
                    <img src={logoPreview} alt="Logo" className="h-12 max-w-[160px] object-contain" />
                    <button onClick={removeLogo} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <span className={`text-xs ${muted}`}>{ep('logoHint')}</span>
                  </div>
                ) : (
                  <button
                    onClick={() => fileRef.current?.click()}
                    data-testid="logo-upload-btn"
                    className={`w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl border-2 border-dashed transition-colors ${
                      isDark ? 'border-slate-600 hover:border-slate-500 text-slate-400' : 'border-gray-300 hover:border-gray-400 text-gray-400'
                    }`}
                  >
                    <ImageIcon className="w-5 h-5" />
                    <span className="text-sm">{ep('logoAdd')}</span>
                  </button>
                )}
                <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={handleLogoChange} />
              </div>

              {/* Report comments */}
              <div>
                <button
                  onClick={() => setCommentsOpen(o => !o)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-colors ${card} ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}
                >
                  <span className={`text-xs font-semibold uppercase tracking-wider ${muted}`}>{ep('comments')}</span>
                  {commentsOpen ? <ChevronUp className="w-4 h-4 opacity-50" /> : <ChevronDown className="w-4 h-4 opacity-50" />}
                </button>
                {commentsOpen && (
                  <div className="mt-2 space-y-3">
                    {[
                      { key: 'intro', label: ep('commentIntro') },
                      { key: 'results', label: ep('commentResults') },
                      { key: 'perimeter', label: ep('commentPerimeter') },
                      { key: 'methodology', label: ep('commentMethodology') },
                      { key: 'objectives', label: ep('commentObjectives') },
                    ].map(({ key, label }) => (
                      <div key={key}>
                        <label className={`block text-xs font-medium mb-1 ${muted}`}>{label}</label>
                        <CommentTextarea
                          rows={3}
                          value={comments[key]}
                          onChange={e => setComments(prev => ({ ...prev, [key]: e.target.value }))}
                          onBlur={e => handleCommentBlur(key, e.target.value)}
                          placeholder="…"
                          className={`w-full rounded-lg border px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            isDark ? 'bg-slate-800 border-slate-600 text-white placeholder-slate-500' : 'bg-white border-gray-200 text-gray-800 placeholder-gray-400'
                          }`}
                          disclaimerClassName={`text-xs italic mt-1 opacity-75 ${muted}`}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Total emissions */}
              <div className={`p-5 rounded-xl border ${card}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-sm font-medium ${muted}`}>{ep('totalEmissions')}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
                    {preview.activities_count} {ep('activities')}
                  </span>
                </div>
                <div className={`text-3xl font-bold ${text}`}>
                  {formatReportTco2e(total, dec)} <span className={`text-base font-normal ${muted}`}>tCO2e</span>
                </div>
              </div>

              {/* Scope breakdown bars */}
              <div className="space-y-2">
                {['scope1', 'scope2', 'scope3_amont', 'scope3_aval'].map(key => {
                  const val = scopes[key] || 0;
                  const pct = total > 0 ? (val / total) * 100 : 0;
                  return (
                    <div key={key} className="flex items-center gap-3">
                      <span className={`text-xs w-24 font-medium ${muted}`}>{t(`dashboard.scopes.${key}`)}</span>
                      <div className={`flex-1 h-3 rounded-full overflow-hidden ${isDark ? 'bg-slate-700' : 'bg-gray-200'}`}>
                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: SCOPE_COLORS[key] }} />
                      </div>
                      <span className={`text-xs font-mono w-20 text-right ${text}`}>{formatReportTco2e(val, dec)} t</span>
                    </div>
                  );
                })}
              </div>

              {/* KPIs row */}
              <div className="grid grid-cols-3 gap-3">
                {kpis.emissions_per_employee && (
                  <div className={`p-3 rounded-xl border text-center ${card}`}>
                    <div className={`text-lg font-bold ${text}`}>{formatReportRatio(kpis.emissions_per_employee)}</div>
                    <div className={`text-xs ${muted}`}>tCO2e/{ep('unitEmployee')}</div>
                  </div>
                )}
                {kpis.emissions_per_revenue_kchf && (
                  <div className={`p-3 rounded-xl border text-center ${card}`}>
                    <div className={`text-lg font-bold ${text}`}>{formatReportRatio(kpis.emissions_per_revenue_kchf)}</div>
                    <div className={`text-xs ${muted}`}>tCO2e/kCHF</div>
                  </div>
                )}
                {kpis.emissions_per_m2 && (
                  <div className={`p-3 rounded-xl border text-center ${card}`}>
                    <div className={`text-lg font-bold ${text}`}>{formatReportRatio(kpis.emissions_per_m2)}</div>
                    <div className={`text-xs ${muted}`}>kgCO2e/m2</div>
                  </div>
                )}
              </div>

              {/* Top categories */}
              {cats.length > 0 && (
                <div>
                  <div className={`flex items-center gap-2 mb-2 ${muted}`}>
                    <BarChart3 className="w-4 h-4" />
                    <span className="text-xs font-semibold uppercase tracking-wider">{ep('topCategories')}</span>
                  </div>
                  <div className="space-y-1.5">
                    {cats.slice(0, 5).map((c) => (
                      <div key={c.code} className="flex items-center justify-between">
                        <span className={`text-sm ${text}`}>{c.label}</span>
                        <span className={`text-sm font-medium ${text}`}>{formatReportTco2e(c.emissions_tco2e, dec)} t ({c.percentage}%)</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Features */}
              <div className="flex items-center gap-3 flex-wrap">
                {preview.has_market_based && (
                  <span className={`text-xs px-2.5 py-1 rounded-full ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-600'}`}>Dual reporting</span>
                )}
                {preview.has_objective && (
                  <span className={`text-xs px-2.5 py-1 rounded-full ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-600'}`}>{ep('objectives')}</span>
                )}
                {preview.has_products && (
                  <span className={`text-xs px-2.5 py-1 rounded-full ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-600'}`}>{ep('products')}</span>
                )}
              </div>
            </>
          ) : (
            <p className={muted}>{ep('noData')}</p>
          )}
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`} data-testid="export-footer">
          {/* Lot 2 zéro bis (D18/D19) : avertissement au niveau du geste d'export, palier reçu du
              backend (jamais recalculé ici), une ligne compacte, informatif — boutons toujours actifs. */}
          {duplication?.level && (
            <div
              className={`flex items-center gap-2 mb-3 px-3 py-2 rounded-lg border text-xs leading-snug ${
                duplication.level === 'critical'
                  ? isDark ? 'bg-red-500/10 border-red-500/40 text-red-200' : 'bg-red-50 border-red-300 text-red-900'
                  : isDark ? 'bg-amber-500/10 border-amber-500/30 text-amber-200' : 'bg-amber-50 border-amber-200 text-amber-900'
              }`}
              data-testid="duplication-rework-warning"
              data-level={duplication.level}
            >
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span className="min-w-0">
                {fill(reworkMessageKey(duplication), { n: duplication.activities_modified, total: duplication.activities_total })}
              </span>
            </div>
          )}
          <div className="flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
              isDark ? 'text-slate-300 hover:bg-slate-700' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {ep('close')}
          </button>
          <button
            data-testid="export-excel-btn"
            onClick={() => handleExport('excel')}
            disabled={!!exporting || loading}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all border ${
              isDark ? 'border-slate-600 text-slate-200 hover:bg-slate-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            } disabled:opacity-50`}
          >
            {exporting === 'excel' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 text-green-600" />}
            Excel
          </button>
          <button
            data-testid="export-appendix-btn"
            onClick={() => handleExport('appendix')}
            disabled={!!exporting || loading}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all border ${
              isDark ? 'border-slate-600 text-slate-200 hover:bg-slate-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            } disabled:opacity-50`}
          >
            {exporting === 'appendix' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSearch className="w-4 h-4 text-indigo-600" />}
            {ep('downloadAppendix')}
          </button>
          <button
            data-testid="export-pdf-btn"
            onClick={() => handleExport('pdf')}
            disabled={!!exporting || loading}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-600/20 transition-all disabled:opacity-50"
          >
            {exporting === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            {ep('downloadPdf')}
          </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportPreviewModal;
